import React, { useEffect, useRef, useState } from 'react';

import { AjaxListingContext } from '$partials/base/ajax-listing';
import { AreaUnitsProvider } from '$contexts/area-units';
import { ExchangeRateProvider } from '$contexts/exchange-rate';
import { ceilToNearestZero, floorToNearestZero } from '$utilities/maths';
import { CtaBannerBlock, PropertyListingAccordionBlock } from '$partials/modules/blocks';
import { Header, Footer } from '$partials/global';
import { Button } from '$partials/ui';
import { PropertyListingFilters } from '$partials/filters/panels';
import { PropertyListView, PropertyMapView, PropertyMapModal, PropertySortInput } from '$partials/listings';
import { SelectedPropertyListingFilters } from '$partials/filters/selected';
import { useAjaxListing } from '$hooks/ajax-listing';
import { useRangeFilter, useSingleSelectFilter, useMultiSelectFilter, useBooleanFilter, useGenericStateFilter } from '$hooks/filters';
import IconList from '$partials/icons/icon-list';
import IconMap from '$partials/icons/icon-map';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/pages.d.ts').SalePropertyListingPage}
 */
const PropertyListingPage = (props) => {
    const page = props.data.page;

    const [listingView, setListingView] = useState('list');
    const [mapHighlight, setMapHighlight] = useState(null);
    const [mapZoom, setMapZoom] = useState(null);
    const [sortBy, setSortBy] = useState(page.items?.filters?.sort?.value);
    const [standfirstExpanded, setStandfirstExpanded] = useState(false);
    const [standfirstOverflows, setStandfirstOverflows] = useState(false);

    /** @type {React.LegacyRef<HTMLDivElement>} */
    const listingContainerRef = useRef();
    /** @type {React.LegacyRef<HTMLParagraphElement>} */
    const standfirstRef = useRef();

    useEffect(() => {
        const el = standfirstRef.current;
        if (el) {
            setStandfirstOverflows(el.scrollHeight > el.clientHeight);
        }
    }, []);

    const officeFilter = useGenericStateFilter({
        initialValue: page.items?.filters?.officeId?.value
    });

    const mapBoundsFilter = useGenericStateFilter({
        defaultValue: page.items?.filters?.mapBounds?.defaultValue,
        initialValue: page.items?.filters?.mapBounds?.value
    });

    const areasFilter = useMultiSelectFilter({
        initialValue: page.items?.filters?.areas?.value,
        options: page.items?.filters?.areas?.options,
        facets: page.items?.facets.find((f) => f.key === page.items?.filters?.areas?.facet)?.buckets
    });

    const amenitiesFilter = useMultiSelectFilter({
        initialValue: page.items?.filters?.amenities?.value,
        options: page.items?.filters?.amenities?.options,
        facets: page.items?.facets.find((f) => f.key === page.items?.filters?.amenities?.facet)?.buckets
    });

    const bathroomsFilter = useSingleSelectFilter({
        initialValue: page.items?.filters?.bathrooms?.value,
        options: page.items?.filters?.bathrooms?.options
    });

    const bedroomsFilter = useSingleSelectFilter({
        initialValue: page.items?.filters?.bedrooms?.value,
        options: page.items?.filters?.bedrooms?.options
    });

    const priceFilter = useRangeFilter({
        initialValue: [floorToNearestZero(page.items?.filters?.price?.value[0]), ceilToNearestZero(page.items?.filters?.price?.value[1])],
        range: page.items?.filters?.price?.range
    });

    const sizeFilter = useRangeFilter({
        initialValue: [floorToNearestZero(page.items?.filters?.size?.value[0]), ceilToNearestZero(page.items?.filters?.size?.value[1])],
        range: page.items?.filters?.size?.range
    });

    const tenuresFilter = useMultiSelectFilter({
        initialValue: page.items?.filters?.tenures?.value,
        options: page.items?.filters?.tenures?.options,
        facets: page.items?.facets.find((f) => f.key === page.items?.filters?.tenures?.facet)?.buckets
    });

    const typesFilter = useMultiSelectFilter({
        initialValue: page.items?.filters?.types?.value,
        options: page.items?.filters?.types?.options,
        facets: page.items?.facets.find((f) => f.key === page.items?.filters?.types?.facet)?.buckets
    });

    const outdoorFilter = useBooleanFilter({
        disabled: page.items?.filters?.outdoor?.disabled,
        initialValue: page.items?.filters?.outdoor?.value,
    });

    const newHomeFilter = useBooleanFilter({
        disabled: page.items?.filters?.newHome?.disabled,
        initialValue: page.items?.filters?.newHome?.value,
    });

    const listing = useAjaxListing({
        infinite: true,
        items: page.items?.edges,
        path: `/api/v1/listings/${encodeURIComponent(props.data?.uri ?? '')}`,
        page: page.items?.page || 1,
        totalItems: page.items?.total,
        totalPages: page.items?.totalPages,
        query: {
            amenities: amenitiesFilter.value,
            areas: areasFilter.value,
            bathrooms: bathroomsFilter.value,
            bedrooms: bedroomsFilter.value,
            maxPrice: priceFilter.value[1],
            maxSize: sizeFilter.value[1],
            minPrice: priceFilter.value[0],
            minSize: sizeFilter.value[0],
            outdoor: outdoorFilter.value ? 'y' : 'n',
            newHome: newHomeFilter.value ? 'y' : null,
            tenures: tenuresFilter.value,
            types: typesFilter.value,
            officeId: officeFilter.value,
            mapBounds: mapBoundsFilter.value ? [
                mapBoundsFilter.value.nw?.lat,
                mapBoundsFilter.value.nw?.lng,
                mapBoundsFilter.value.se?.lat,
                mapBoundsFilter.value.se?.lng
            ] : undefined,
            mapSearch: mapBoundsFilter.value?.search,
            mapDistance: mapBoundsFilter.value?.distance,
            sort: sortBy
        },
        onData: (data) => {
            areasFilter.setFacets(data?.facets.find((f) => f.key === page.items?.filters?.areas?.facet)?.buckets);
            amenitiesFilter.setFacets(data?.facets.find((f) => f.key === page.items?.filters?.amenities?.facet)?.buckets);
            tenuresFilter.setFacets(data?.facets.find((f) => f.key === page.items?.filters?.tenures?.facet)?.buckets);
            typesFilter.setFacets(data?.facets.find((f) => f.key === page.items?.filters?.types?.facet)?.buckets);
            outdoorFilter.setDisabled(data?.metrics?.find((m) => m.key === 'outdoor_area_feet_max')?.value <= 0);
            newHomeFilter.setDisabled(!data?.facets?.find((f) => f.key === 'new_home__isnull')?.buckets?.some?.((b) => b?.key === '1' && b?.docCount > 0));
            setMapHighlight(null);
        }
    });

    /**
     * Change between list and map views
     * @type {(view: ('list' | 'map')) => void} */
    const handleViewChange = (view) => {
        listingContainerRef.current.scrollIntoView({
            alignToTop: true,
            behaviour: 'instant'
        });

        setListingView(view);
        setMapHighlight(null);
    };

    return (
        <AreaUnitsProvider defaultUnit={page?.parent?.areaUnit === 'sqm' || page?.parent?.areaUnit === 'sqft' ? page.parent.areaUnit : undefined}>
            <ExchangeRateProvider
                defaultCurrency={page.parent?.priceCurrency}
                exchangeRate={props.data?.exchangeRates}>
                <Header {...props.data.header} />

                <main id='main-content'>
                    <AjaxListingContext.Provider value={listing}>

                        {/* page header */}
                        <header className='theme-light-alt bg-main-background'>
                            <div className='layout-container layout-site-outer-padding flex'>
                                <h1 className='type-style-1 mt-[76px] md:mt-[160px] mb-[48px]'>
                                    {page.title}
                                </h1>
                            </div>

                            {/* Filters */}
                            <PropertyListingFilters
                                amenitiesFilter={amenitiesFilter}
                                areasFilter={areasFilter}
                                bathroomsFilter={bathroomsFilter}
                                bedroomsFilter={bedroomsFilter}
                                dialogFilterSelection={page.dialogFilters}
                                mapBoundsFilter={mapBoundsFilter}
                                newHomeFilter={newHomeFilter}
                                officeFilter={officeFilter}
                                outdoorFilter={outdoorFilter}
                                priceFilter={priceFilter}
                                promotedFilterSelection={page.promotedFilters}
                                setSortBy={setSortBy}
                                sizeFilter={sizeFilter}
                                sortBy={sortBy}
                                tenuresFilter={tenuresFilter}
                                typesFilter={typesFilter} />
                        </header>

                        <section
                            className='theme-light bg-main-background pt-24 pb-40'
                            ref={listingContainerRef}>

                            {/* Selected filters */}
                            <SelectedPropertyListingFilters
                                amenitiesFilter={amenitiesFilter}
                                areasFilter={areasFilter}
                                bathroomsFilter={bathroomsFilter}
                                bedroomsFilter={bedroomsFilter}
                                mapBoundsFilter={mapBoundsFilter}
                                newHomeFilter={newHomeFilter}
                                officeFilter={officeFilter}
                                outdoorFilter={outdoorFilter}
                                priceFilter={priceFilter}
                                sizeFilter={sizeFilter}
                                tenuresFilter={tenuresFilter}
                                typesFilter={typesFilter} />

                            {page.standfirst && (
                                <div className='hidden md:block layout-container layout-site-outer-padding pb-32'>
                                    <div className='col-wrapper'>
                                        <div className='w-full md:w-8/12 col-padding text-main-text-secondary type-style-body-l'>
                                            <p
                                                className={`${!standfirstExpanded ? ' line-clamp-2' : ''}`}
                                                ref={standfirstRef}>
                                                {page.standfirst}
                                            </p>
                                            {standfirstOverflows && (
                                                <Button
                                                    as='button'
                                                    className='underline underline-offset-2 decoration-1 mt-16'
                                                    onClick={() => setStandfirstExpanded(!standfirstExpanded)}
                                                    type='button'>
                                                    {standfirstExpanded ? 'Read Less' : 'Read More'}
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className='layout-container layout-site-outer-padding flex justify-between items-center'>

                                <aside className='type-style-eyebrow'>
                                    {`${listing.totalItems} result${listing.totalItems !== 1 ? 's' : ''}`}
                                </aside>

                                <div>
                                    <PropertySortInput
                                        allowDistance={Boolean(mapBoundsFilter.value?.search)}
                                        className='!p-0 !h-[auto] !bg-[transparent]'
                                        onChange={setSortBy}
                                        value={sortBy} />
                                </div>
                            </div>

                            {/* Standard list view */}
                            <div className={(listingView === 'list') ? '' : 'sm:hidden'}>
                                <PropertyListView
                                    featuredProperty={page?.featuredProperty}
                                    isUnfiltered={(areasFilter.value.length === 0 &&
                                        !mapBoundsFilter.value &&
                                        !officeFilter.value &&
                                        !outdoorFilter.value &&
                                        amenitiesFilter.value.length === 0 &&
                                        bathroomsFilter.value === null &&
                                        bedroomsFilter.value === null &&
                                        priceFilter.value[0] === priceFilter.range[0] &&
                                        priceFilter.value[1] === priceFilter.range[1] &&
                                        sizeFilter.value[0] === sizeFilter.range[0] &&
                                        sizeFilter.value[1] === sizeFilter.range[1] &&
                                        tenuresFilter.value.length === 0 &&
                                        typesFilter.value.length === 0)}
                                    listing={listing} />
                            </div>

                            {/* Desktop map view */}
                            {(mapBoundsFilter.defaultValue?.centre && listingView === 'map') &&
                                <div className='hidden sm:block'>
                                    <PropertyMapView
                                        defaultBounds={mapBoundsFilter.value}
                                        defaultCentre={mapBoundsFilter?.defaultValue?.centre}
                                        highlight={mapHighlight}
                                        listing={listing}
                                        onHighlightChange={setMapHighlight}
                                        onSearch={(bounds) => mapBoundsFilter.setValue(bounds)}
                                        onZoomChange={setMapZoom}
                                        zoom={mapZoom} />
                                </div>
                            }

                            {(mapBoundsFilter.defaultValue?.centre) &&
                                <>
                                    {/* View toggle */}
                                    <div className='hidden sm:block sticky bottom-0 left-0 right-0 text-center pointer-events-none z-10 pb-16 md:pb-24'>
                                        <button
                                            className='pointer-events-auto button-primary-filled type-style-eyebrow flex items-center !gap-8 h-[32px] sm:h-auto'
                                            onClick={() => handleViewChange(listingView === 'map' ? 'list' : 'map')}
                                            type='button'>
                                            {(listingView === 'map') ? (<IconList />) : (<IconMap />)}
                                            <span>{(listingView === 'map') ? 'List' : 'Map'}</span>
                                        </button>
                                    </div>

                                    {/* Mobile map view */}
                                    <PropertyMapModal
                                        amenitiesFilter={amenitiesFilter}
                                        areasFilter={areasFilter}
                                        bathroomsFilter={bathroomsFilter}
                                        bedroomsFilter={bedroomsFilter}
                                        defaultCentre={mapBoundsFilter?.defaultValue?.centre}
                                        dialogFilterSelection={page.dialogFilters}
                                        highlight={mapHighlight}
                                        listing={listing}
                                        mapBoundsFilter={mapBoundsFilter}
                                        officeFilter={officeFilter}
                                        onHighlightChange={setMapHighlight}
                                        onSearch={(bounds) => mapBoundsFilter.setValue(bounds)}
                                        onZoomChange={setMapZoom}
                                        outdoorFilter={outdoorFilter}
                                        priceFilter={priceFilter}
                                        promotedFilterSelection={page.promotedFilters}
                                        sizeFilter={sizeFilter}
                                        tenuresFilter={tenuresFilter}
                                        typesFilter={typesFilter}
                                        zoom={mapZoom} />
                                </>
                            }
                        </section>

                    </AjaxListingContext.Provider>
                </main>

                {page.textAccordion?.[0] && (
                    <PropertyListingAccordionBlock
                        heading={page.textAccordion[0].heading}
                        items={page.textAccordion[0].items}
                        subheading={page.textAccordion[0].subheading} />
                )}

                <CtaBannerBlock
                    body={props.data.footer?.getInTouchBodyText}
                    heading={props.data.footer?.getInTouchHeadingText}
                    opensDialog={true} />

                <Footer {...props.data.footer} />
            </ExchangeRateProvider>
        </AreaUnitsProvider>
    );
};

/* ========================================================================= */
/* Exports */
/* ========================================================================= */

export default PropertyListingPage;
